import { useContext } from 'react';
import { TenantContext } from '../../../context/tenant-context';
import { Job, JobConfig, JobConfigRequest, JobStatus, JobType } from './job-tasks';
import { handleRequest } from '../quipli-service-client';
import { JobTaskParsers } from './job-task-parsers';

export const useJobTaskApi = () => {
  const { tenant, contextPath } = useContext(TenantContext);

  async function listJobConfigs(): Promise<JobConfig[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/inspections/configs`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return handleRequest<JobConfig[]>(config);
  }

  async function getReport(jobId: string, unitId: number): Promise<BlobPart> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/inspections/jobs/${jobId}/report/${unitId}`,
      headers: {
        'X-Tenant': tenant,
      },
      responseType: 'blob' as const,
    };
    return handleRequest(config, true);
  }

  async function upsertJobConfig(req: JobConfigRequest): Promise<JobConfig> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/inspections/configs`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
    return handleRequest<JobConfig>(config);
  }

  async function deleteJobConfig(id: string) {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/v1/inspections/configs/${id}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return handleRequest(config);
  }

  async function getProductCategoryJobConfig(entityType: 'Product' | 'Category', entityId: number, jobType: JobType) {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/inspections/configs/product-category-job-config?entityType=${entityType}&entityId=${entityId}&jobType=${jobType}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return (await handleRequest<{ jobType: JobType; jobConfigId: string }[]>(config))[0];
  }

  async function getProductJobConfigs(jobType: JobType, productId: number) {
    const params = new URLSearchParams();
    params.append('jobType', jobType);
    params.append('productId', productId.toString());

    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/inspections/configs/product-job-configs?${params.toString()}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return handleRequest<JobConfig[]>(config);
  }

  async function setProductCategoryJobConfig(
    entityType: 'Product' | 'Category',
    entityId: number,
    jobConfigId: string | undefined,
    jobType: JobType,
  ) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/inspections/configs/product-category-job-config`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { entityType, entityId, jobType, jobConfigId },
    };
    return handleRequest(config);
  }

  async function startJob(orderId: number, jobType: JobType): Promise<Job> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/inspections/jobs/start`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { orderId, jobType },
    };
    return handleRequest<Job>(config);
  }

  async function getJobByEntity(entityType: string, entityId: string, jobType: JobType): Promise<Job> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/inspections/jobs?entityType=${entityType}&entityIds=${entityId}${
        jobType ? `&jobType=${jobType}` : ''
      }`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const res = await handleRequest<JobTaskParsers.JobJson[]>(config);
    return res.map(JobTaskParsers.parseJob)[0];
  }

  async function getJobsByMultipleEntityIds(
    entityType: string,
    entityIds: string | string[],
    jobType?: JobType,
  ): Promise<Job[]> {
    const ids = Array.isArray(entityIds) ? entityIds : [entityIds];

    const params = new URLSearchParams();
    params.append('entityType', entityType);
    params.append('entityIds', ids.join(','));
    if (jobType) {
      params.append('jobType', jobType);
    }

    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/inspections/jobs?${params.toString()}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const res = await handleRequest<JobTaskParsers.JobJson[]>(config);
    return res.map(JobTaskParsers.parseJob);
  }

  async function updateTask(
    jobId: string,
    taskId: string,
    status: JobStatus,
    metadata: Record<string, any>,
  ): Promise<Job> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/inspections/jobs/${jobId}/tasks/${taskId}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { status, metadata },
    };
    return handleRequest<Job>(config);
  }

  return {
    listJobConfigs,
    upsertJobConfig,
    deleteJobConfig,
    setProductCategoryJobConfig,
    getProductCategoryJobConfig,
    getProductJobConfigs,
    startJob,
    updateTask,
    getJobByEntity,
    getJobsByMultipleEntityIds,
    getReport,
  };
};
